import React, { useState, useRef, useEffect } from "react";
import {
  Send,
  Mic,
  StopCircle,
  Loader2,
  Globe,
  Code,
  Brain,
  Database,
} from "lucide-react";
import { useVoiceRecorder } from "../../hooks/useVoiceRecorder";
import { notify } from "../../utils/toast";
// import { useAuth } from "../../hooks/chat/useAuth";
import {  getAuthData } from "../../contexts/AuthContext";
import { getAiApiUrl } from "../../config";
import { AudioWaveform } from "./AudioWaveform";

import { ToolMode, DEFAULT_TOOL_MODES } from "../../constants/toolModes";

interface ChatInputProps {
  onSend: (content: string) => Promise<void>;
  onStop?: () => void;
  disabled?: boolean;
  sending?: boolean;
  onToolModesChange?: (toolModes: ToolMode[]) => void;
  initialTools?: ToolMode[];
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  onStop,
  disabled,
  sending,
  onToolModesChange,
  initialTools = DEFAULT_TOOL_MODES,
}) => {
  const [message, setMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const {
    isRecording,
    startRecording,
    stopRecording,
    mediaRecorder,
    audioBlob,
  } = useVoiceRecorder();

  const [toolModes, setToolModes] = useState<ToolMode[]>(initialTools);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Update local state when initialTools change
  useEffect(() => {
    setToolModes(initialTools);
  }, [initialTools]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        150
      )}px`;
    }
  }, [message]);

  const handleSubmit = async () => {
    if (!message.trim() || disabled) return;
    const content = message;
    setMessage(""); // Clear input immediately after submission
    await onSend(content);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const toggleTool = (index: number) => {
    setToolModes((prevToolModes) => {
      // Check if trying to activate a non-MCP tool while MCP mode is active
      // if (prevToolModes[3].active && index !== 3) {
      //   notify.info(
      //     `To activate this mode, first disable MCP mode!`,
      //     4000
      //   );
      //   return prevToolModes; // Don't change tool Modes state
      // }

      // Check if trying to activate MCP Mode while other tool Modes are active
      // if (index === 3 && !prevToolModes[3].active && prevToolModes.some((toolMode, i) => i !== 3 && toolMode.active)) {
      //   notify.info(
      //     `Activating MCP mode will disable other modes!\n\nTo Activate Web Search, Coder or Deep Think first disable MCP mode`,
      //     6000
      //   );
      // }

      const newToolModes = prevToolModes.map((tool, i) => {
       return {
            ...tool,
            active:
              i === index
                ? !tool.active
                : tool.active,
          };
      });
      
      // Immediately notify parent component about tool mode changes
      if (onToolModesChange) {
        onToolModesChange(newToolModes);
      }
      
      return newToolModes;
    });
  };

  return (
    <div className="space-y-3">
      <div className="relative flex w-full">
        <div className="flex-1 min-w-0">
          {isRecording || isProcessing ? (
            <div className="w-full px-4 py-3 pr-[120px] bg-gray-100 dark:bg-gray-700 border-0 rounded-2xl min-h-[44px] max-h-[150px]">
              <AudioWaveform
                mediaRecorder={mediaRecorder}
                audioBlob={audioBlob}
                isRecording={isRecording}
              />
            </div>
          ) : (
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter your prompt..."
              disabled={disabled}
              className="w-full px-4 py-3 pr-[120px] bg-gray-100 dark:bg-gray-700 border-0 rounded-2xl focus:ring-2 focus:ring-blue-500 resize-none transition-shadow text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 min-h-[44px] max-h-[150px] leading-normal disabled:opacity-50 disabled:cursor-not-allowed"
              rows={1}
            />
          )}
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center space-x-1">
            {/* <button
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              title="Add attachment"
            >
              <Paperclip size={20} />
            </button> */}
            <button
              onClick={async () => {
                if (isRecording) {
                  try {
                    const audioBlob = await stopRecording();
                    if (audioBlob) {
                      // Wait for 1 second to show the static waveform
                      await new Promise((resolve) => setTimeout(resolve, 1000));

                      setIsProcessing(true);
                      const auth = await getAuthData();

                      const formData = new FormData();
                      formData.append("file", audioBlob);
                      formData.append("walletAddress", auth.walletAddress);
                      formData.append("nonce", auth.nonce);
                      formData.append("user", auth.did.toLowerCase().replaceAll('_uuid', ':uuid').replaceAll('_addr', ':addr').replaceAll('_asa', ':asa').replaceAll('_app', ':app').replaceAll('_txn', ':txn'));

                      const response = await fetch(
                        `${getAiApiUrl()}/audio-to-text`,
                        {
                          method: "POST",
                          headers: {
                            Authorization: `Bearer ${auth.jwt}`,
                          },
                          body: formData,
                        }
                      );

                      if (!response.ok) {
                        throw new Error("Failed to convert audio to text");
                      }

                      const { text } = await response.json();
                      if (text) {
                        setMessage(text);
                      }
                    }
                  } catch (error) {
                    console.error("Audio processing error:", error);
                    notify.error("Failed to process voice message");
                  } finally {
                    setIsProcessing(false);
                  }
                } else {
                  setMessage(""); // Clear input before starting recording
                  await startRecording();
                }
              }}
              disabled={disabled || isProcessing}
              className={`p-2 transition-colors ${
                isRecording
                  ? "text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              } ${
                disabled || isProcessing ? "opacity-50 cursor-not-allowed" : ""
              }`}
              title={isRecording ? "Stop recording" : "Voice message"}
            >
              <div className="relative w-[20px] h-[20px]">
                {isProcessing && (
                  <div className="absolute -inset-2 flex items-center justify-center">
                    <Loader2 size={32} className="animate-spin text-blue-500" />
                  </div>
                )}
                <div
                  className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${
                    isProcessing ? "opacity-0" : "opacity-100"
                  }`}
                >
                  {isRecording ? <StopCircle size={20} /> : <Mic size={20} />}
                </div>
              </div>
            </button>
            {sending ? (
              <button
                onClick={onStop}
                className="p-2 bg-red-600 dark:bg-red-500 hover:bg-red-700 dark:hover:bg-red-600 rounded-full transition-colors"
                title="Stop generating"
              >
                <StopCircle size={20} className="text-white" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!message.trim() || disabled}
                className="p-2 bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 rounded-full transition-colors"
              >
                <Send size={20} className="text-white" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex w-full overflow-x-auto gap-2 pb-1 -mx-2 px-2 scrollbar-custom-horizontal">
        <div className="flex gap-2 min-w-0 flex-nowrap">
          {toolModes.map((toolMode, index) => (
            <div key={index} className="flex-shrink-0 relative group">
              <button
                // disabled={tool.name === 'Web Search'}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleTool(index);
                }}
                className={`flex px-4 py-3 rounded-lg items-center transition-colors whitespace-nowrap ${
                  toolMode.active
                    ? " text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800 transition-all duration-200 transform"
                    : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-blue-500/10 hover:via-purple-500/10 hover:to-pink-500/10 dark:hover:from-blue-500/20 dark:hover:via-purple-500/20 dark:hover:to-pink-500/20 text-gray-700 dark:text-gray-300"
                } md:px-3 md:py-1.5 p-2 rounded-full md:rounded-lg space-x-0 md:space-x-2`}
              >
                 <div className="absolute inset-0 -z-10 overflow-hidden rounded-lg">
            {/* Base layer with glass shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent bg-[length:200%_100%] animate-glass-shine-mode rounded-lg"></div>
            
            {/* Second layer with horizontal movement */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/15 via-transparent to-purple-500/15 bg-[length:200%_100%] animate-gradient-x rounded-lg"></div>
            
            {/* Third layer with vertical movement */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-pink-500/15 to-transparent bg-[length:100%_200%] animate-gradient-y rounded-lg"></div>
            
            {/* Fourth layer with diagonal movement */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-pink-500/10 bg-[length:200%_200%] animate-ambient-move rounded-lg opacity-70"></div>
          </div>
                <toolMode.icon size={20} className="flex-shrink-0" />
                <span className="hidden md:inline text-sm">{toolMode.name}</span>
              </button>
              {/* Mobile Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none md:hidden">
                {toolMode.name}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
